import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GavelIcon from '@mui/icons-material/Gavel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useDropzone } from 'react-dropzone';
import { alpha } from '@mui/material/styles';
import firebase from '../../../utilities/firebase';
import { StateManager } from '../../../utilities/stateManager';

const BUCKET = 'skyway-dev-373d5.appspot.com';

export default function SignedPacketSlot({ car = {}, disabled }) {
  const [loading, setLoading] = React.useState(false);
  const [fileLoaded, setFileLoaded] = React.useState(!!car.sales_doc);
  const [filepath, setFilepath] = React.useState(car.sales_doc);

  React.useEffect(() => {
    setFileLoaded(!!car.sales_doc);
    setFilepath(car.sales_doc);
  }, [car.sales_doc]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: false,
    accept: 'image/*,.pdf',
    disabled: disabled || loading || fileLoaded,
    onDrop: async (files) => {
      if (!files.length) return;
      const file = files[0];
      setLoading(true);
      const ext = file.name.split('.').pop();
      const filename = `deals/${car.id || car.stock}-sales_doc.${ext}`;
      const storageRef = firebase.app().storage(BUCKET).ref(filename);
      StateManager.setAlertAndOpen('Upload started — you can keep working.', 'info');
      StateManager.uploadFile({ storageRef, file }).then(async (result) => {
        if (result) {
          setFileLoaded(true);
          setFilepath(filename);
          await firebase.firestore().doc(`cars/${car.stock}`).set({ sales_doc: filename }, { merge: true });
        }
        setLoading(false);
      });
    },
  });

  const viewFile = async () => {
    if (!filepath) return;
    const url = await firebase.app().storage(BUCKET).ref(filepath).getDownloadURL();
    window.open(url, '_blank');
  };

  return (
    <Box sx={{ p: 2 }}>
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1.5 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>Uploading...</Typography>
        </Box>
      ) : fileLoaded ? (
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          p: 2, borderRadius: 1.5,
          border: 1, borderColor: 'success.light',
          bgcolor: (t) => alpha(t.palette.success.main, 0.07),
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 24 }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.dark' }}>Signed Packet on File</Typography>
              <Typography variant="caption" sx={{ color: 'success.main' }}>Document uploaded</Typography>
            </Box>
          </Box>
          <Button size="small" variant="outlined" color="success" startIcon={<OpenInNewIcon />} onClick={viewFile} data-sold-ok>
            View
          </Button>
        </Box>
      ) : (
        <Box
          {...getRootProps()}
          sx={{
            p: 3,
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'divider',
            borderRadius: 1.5,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
            cursor: disabled ? 'default' : 'pointer',
            bgcolor: isDragActive ? (t) => alpha(t.palette.primary.main, 0.05) : 'transparent',
            transition: 'border-color 0.15s, background-color 0.15s',
            '&:hover': disabled ? {} : {
              borderColor: 'primary.main',
              bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
            },
          }}
        >
          <input {...getInputProps()} />
          <GavelIcon sx={{ fontSize: 30, color: 'text.disabled' }} />
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            {isDragActive ? 'Drop to upload' : 'Upload Signed Packet'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>PDF or image</Typography>
        </Box>
      )}
    </Box>
  );
}
